import { HttpErrorResponse, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest, HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { filter, take, catchError, switchMap, finalize } from 'rxjs/operators';
import { AuthenticationService } from '../authentication/authentication.service';
import { ToastrService } from 'ngx-toastr';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private readonly cachedRequests: Array<HttpRequest<any>> = [];
  private isRefreshingToken = false;
  private readonly tokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  token = '';

  constructor(
    private readonly toastr: ToastrService,
    public jwtHelper: JwtHelperService,
    private readonly authenticationService: AuthenticationService
  ) {}

  
  /**
   * Get token from session
   * returns token
   */
  public getToken(): string {
    return sessionStorage.getItem('token');
  }
  
  /**
   *  to check user is authenticated or not
   */
  public isAuthenticated(): boolean {
    // get the token
    const token = this.getToken();
    return !!token;
  }

  public collectFailedRequest(request): void {
    this.cachedRequests.push(request);
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    const token = sessionStorage.getItem('token');
    // IE11 caching remove
    const nextReq = request.clone({
      headers: request.headers.set('Cache-Control', 'no-cache')
        .set('Pragma', 'no-cache')
        .set('Expires', 'Sat, 01 Jan 2000 00:00:00 GMT')
        .set('If-Modified-Since', '0')
    });

    return next
    .handle(nextReq)
    // .handle(this.addToken(request, this.getToken(), isTokenRequired))
    .pipe(catchError((err) => {
          const message = {
            status: err.statusText,
            message: err.message,
            error: err.error
          };

          const four = 400;
          const fourOne = 401;
          const fourThree = 403;

          if (err instanceof HttpErrorResponse) {
            this.collectFailedRequest(nextReq);
            if (err.status === fourOne) {
              // Check for token expired
              if (this.jwtHelper.isTokenExpired(token) === true) {
                // Handling the 401 error for refresh token
                return this.handle401Error(nextReq, next);
              } else {
                 if (err.error === 'Unauthorized' || err.statusText === 'Unauthorized') {
                    this.authenticationService.logout();
                  }
              }
            } else if (err.status === fourThree) {
              this.toastr.error(err.message, '', {
                timeOut: 5000
              });
            } else if (err.status === four) {
              this.toastr.error('Bad Request', message.error);
            } else {
                 //Added To SonarQube Fix
            }
          }
          return throwError(err);
        })
      );
  }

   /**
   * error handler for 401 error code
   */
  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;
      this.tokenSubject.next(null);
      // calling refreh token api
      return this.authenticationService.refreshToken().pipe(
        switchMap(data => {
          const token = data._body;
          if (token) {
            sessionStorage.setItem('token', token);
            const context = { idms: this.authenticationService.ParseJwt(token.toString()) };
            this.authenticationService.setContext(context);
            this.tokenSubject.next(token);
            return next.handle(this.addToken(request, token, true));
          }
          // unauthorized user logged out
          return this.authenticationService.logout();
        }),
        catchError((_error) => {
          return this.authenticationService.logout();
        }),
        finalize(() => {
          this.isRefreshingToken = false;
        })
      );
    } else {
      this.isRefreshingToken = false;
      return this.tokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((token) => {
          return next.handle(this.addToken(request, token, false));
        })
      );
    }
  }
  
    /**
   * add token in every request
   */
  private addToken(req: HttpRequest<any>, token: string, isTokenRequired: boolean): HttpRequest<any> {
    let headers: HttpHeaders = new HttpHeaders();
    if (isTokenRequired) {
      headers = headers.append('Authorization', 'Bearer ' + token);
    }
    headers = headers.set('offset', new Date().getTimezoneOffset().toString());
    return req.clone({ headers });
  }
}
